import Gardener from 'scoreboard/components/gardener'
import _ from 'lodash'
import * as AddTypes from 'scoreboard/components/add-types'
const { CHILD, SIBLING, ROOT } = AddTypes
import FormType from './model/form-type'
const { FORM, PAGE, FOLDER, DIALOG, NAVIGATION_TABS } = FormType
import unwrapModel from 'scoreboard/components/unwrap-model'
import formsPasteAllowedFactory from './forms-paste-allowed-factory'
import { Transform } from 'stream'
import Backbone from 'backbone'
import getParentForm from './get-parent-form'
import conductor from 'scoreboard/components/conductor'

const FormsGardener = Gardener.extend({

	_bindEvents: function () {
		Gardener.prototype._bindEvents.apply(this, arguments)

		this.on('change:editViewState', (edit, opts) => {
			this.onChangeEditViewState(edit, opts)
		})

		this.tree.on('move', this.move.bind(this))
	},

	/**
	 * Retrieves the position where a node should be added in the tree based
	 * on the type of object it and the ID it should be added relative to.
	 * We need to override the normal logic to prevent folders from being added
	 * under forms (#35094)
	 */
	getAddPosition: function(model, relativeId, mode) {
		let relative = this.tree.get(relativeId)
		// when the user chooses "new folder" or "new form" with a page or
		// navigation tabs selected, need to add the folder as a sibling of the
		// parent form selected
		if ([FOLDER, FORM].includes(unwrapModel(model).type) &&
			[PAGE, NAVIGATION_TABS, DIALOG].includes(relative?.type)) {
			let form = getParentForm(this.tree, relative.parentId)
			// the new folder should be a sibling after the parent form
			return this._getNextSiblingPosition(form, form.id)
		}
		// handle adding navigation tabs relative to pages/dialogs
		if (unwrapModel(model).type === NAVIGATION_TABS &&
			(relative?.type === PAGE || relative?.type === DIALOG)) {
			// if the page is under a form
			let parent = this.tree.parent(relativeId)
			if (parent?.type == FORM) {
				// add the navigation tabs as a sibling of the page
				return this._getNextSiblingPosition(relative, relativeId)
			}
			// if the page is under navigation tabs
			if (parent?.type == NAVIGATION_TABS) {
				// add the navigation tabs as a sibling of the parent navigation
				//tabs
				return this._getNextSiblingPosition(parent, parent.id)
			}
		}
		return Gardener.prototype.getAddPosition.apply(this, arguments)
	},

	/**
	 * Based on the type of the selected item and the type of item about
	 * to be newly created, returns whether the newly created item should be
	 * positioned as a CHILD or SIBLING

	 * @param relative
	 *            the tree node attributes representing the relative item
	 * @param newItem
	 *            the model of the new item being added
	 * @return {string} CHILD or SIBLING or ROOT
	 */
	getAddType: function (relative, newItem) {
		relative = unwrapModel(relative)
		newItem = unwrapModel(newItem)

		if (relative.type == NAVIGATION_TABS) {
			return newItem.type == PAGE ? CHILD : SIBLING
		}

		if (newItem.type === FORM) {
			if (relative.type === FOLDER) {
				return CHILD
			}
			if (relative.type === FORM) {
				return SIBLING
			}
			if (relative.type === PAGE) {
				return ROOT
			}
		}

		if (newItem.type === PAGE) {
			if (relative.type === FORM) {
				return CHILD
			}
			if (relative.type === PAGE) {
				return SIBLING
			}
			return ROOT
		}

		if (newItem.type === DIALOG) {
			return CHILD
		}

		if (newItem.type === FOLDER) {
			// folders should normally be added as a sibling
			// (matches the logic from new-folder-add-type.js)
			return SIBLING
		}

		if (newItem.type == NAVIGATION_TABS) {
			return CHILD
		}

		throw new Error('Unable to determine new position.  relative.type: ' +
			relative.type + ', newItem.type: ' + newItem.type)
	},

	_treeOptions: function () {
		// can't just pass the tree in directly b/c it isn't defined yet
		let formsPasteAllowed = formsPasteAllowedFactory(() => this.tree)

		let treeOptions = Gardener.prototype._treeOptions.apply(this, arguments)
		treeOptions.droppable = formsPasteAllowed.bind(formsPasteAllowed)
		treeOptions.stream = treeOptions.stream.pipe(this._transform())
		return treeOptions
	},

	/**
	 * Returns a Transform that toggles the visibility of form nodes that have
	 * a type of 'page' and 'navigation-tabs'
	 */
	_transform: function () {
		const isEditing = this.options?.edit
		let transform = new Transform({objectMode: true})
		transform._transform = function (node, enc, next) {
			node.visible = isEditing ??
				![PAGE, NAVIGATION_TABS, DIALOG].includes(node?.type)
			this.push(node)
			next()
		}
		return transform
	},

	/**
	 * @override
	 * @param {Object} options - the options used to perform the paste
	 * @param {Map<Int,Int>} ids - a hash of old IDs to new IDs
	 */
	_copyDomainObject: function(options, ids) {
		Gardener.prototype._copyDomainObject.apply(this, arguments)

		// #52583 make sure that the child nodes of newly copied node have the correct parent
		// id populated so that drag and drop rules can be correctly enforced immediately
		// after the copy occurs
		let copyId = ids[options.id]
		if (!_.isNil(copyId)) {
			let node = this.tree.get(copyId)
			// #53030 this does not apply to a duplicated page/dialog,
			// only needed when copying a folder or form
			// #53179 only update if the copied node is found in the tree
			// (ex. if the form was copied to another org, 'node' will be
			// undefined for this tree, and updating the descendants is not needed)
			if (node && node.type !== PAGE && node.type !== DIALOG) {
				this.updateDescendantParentId(node, copyId)
			}
		}
	},

	/**
	 * Updates the parentId for the given node and its descendants
	 */
	updateDescendantParentId(node, parentId) {
		if (node.id != parentId) {
			// #60374 don't set a node as its own parent
			node.parentId = parentId
		}
		_.each(this.tree.children(node), child => {
			this.updateDescendantParentId(child, node.id)
		})
	},

	/**
	 * Updates the visibility of form nodes when the editViewState
	 * changes
	 */
	onChangeEditViewState: function (edit, opts={}) {
		let patch = []
		const isNew = conductor.path().indexOf('/new') != -1 ||
			opts.isNew
		for (let id in this.tree.nodes) {
			let node = this.tree.nodes[id]
			let v = this.visible(node, edit, { isNew })
			if (v !== node.visible) {
				node.visible = v
				patch[id] = node
			}
		}
		this.tree.edit(patch)
	},

	/**
	 * Tests whether a form node should be visible in the current mode
	 *
	 * @param {Boolean} [opts.isNew] whether display is showing a new form
	 */
	visible: function (node, edit, opts={}) {
		const isPageOrTab = [PAGE, NAVIGATION_TABS, DIALOG].includes(node?.type)
		if (edit) {
			// if this is a new page or tab, it should be visible because
			// it's being created
			if (node.id < 0) return true

			// if this is a new form, just show the pages and tabs that
			// are already visible
			if (opts.isNew) return node.visible

			let selected = this.tree.selected() ?? {}
			const rootOfSelectedId = getParentForm(this.tree, selected.id)?.id
			const rootOfPageOrTabId = getParentForm(this.tree, node.id)?.id

			// #58405 show pages, tabs, and dialogs in search when editing
			// #53598 show a page or tab if it is already visible, if it
			// belongs to the same root as what we are viewing or if it
			// is currently selected
			return isPageOrTab || node.visible ||
				(rootOfSelectedId === rootOfPageOrTabId) ||
				node.id == selected?.id
		} else if (isPageOrTab) {
			return false
		}
		return true
	},

	move: function(node, newParent) {
		Gardener.prototype.move.apply(this, arguments)
		if (_.isNil(newParent)) {
			return
		}
		const children = this.tree.children(newParent.id)
		let pageOrder = _.map(children, c => c.id)
		Backbone.trigger(`move:page`, { rootFormId: newParent.id, pageOrder })
	}

})

export default FormsGardener
FormsGardener.__filename__ = 'forms/forms-gardener.js'
